import PageWrapper from "@/components/layout/page-wrapper";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { AlignLeft, ChevronDown, Pencil, Search, SquareMenuIcon, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useNavigate } from "react-router";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CreatePackageDialog from "./create-package-dialog";
import { useGetAllFeaturePlans } from "@/api/plan/get-all-feature-plans";
import { cn, formatDate } from "@/lib/utils";
import { useState, useRef, useEffect } from "react";
import UpdatePackageDialog from "./update-package-dialog";
import DeletePackageDialog from "./delete-package-dialog";
import FullscreenLoading from "@/components/layout/fullscreen-loading";
import { Input } from "@/components/ui/input";
import { useSearchParams } from "react-router";

const BillingTableDropdown: React.FC<{ id: string }> = ({ id }) => {
    const navigate = useNavigate();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger>
                    <Button size={"sm"} variant="ghost" className="p-2 cursor-pointer">
                        <SquareMenuIcon />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`${id}`)}>
                        <AlignLeft /> View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
                        <Pencil /> Update
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)}>
                        <Trash /> Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            {editDialogOpen && <UpdatePackageDialog id={id} dialogOpen={editDialogOpen} onClose={() => setEditDialogOpen(false)} />}
            {deleteDialogOpen && <DeletePackageDialog id={id} dialogOpen={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} />}
        </>
    );
};

const PackageManage = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Extract parameters from URL
    const search = searchParams.get("search") || null;
    const isActiveParam = searchParams.get("is_active");
    const is_active = isActiveParam && isActiveParam !== "all" ? isActiveParam : undefined;

    const { data: featurePlans, isLoading, error } = useGetAllFeaturePlans({
        search,
        is_active
    });

    const formRef = useRef<HTMLFormElement>(null);
    const statusRef = useRef<HTMLSelectElement>(null);

    useEffect(() => {
        if (searchParams.get("search") && formRef.current) {
            (formRef.current).search.value = searchParams.get("search") || "";
        }

        // Set the initial status dropdown value based on URL parameters
        if (statusRef.current) {
            const currentStatus = searchParams.get("is_active");
            if (currentStatus === "true" || currentStatus === "false") {
                statusRef.current.value = currentStatus;
            } else {
                statusRef.current.value = "all";
            }
        }
    }, [searchParams]);

    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const searchValue = form.search.value.trim();
        const statusValue = statusRef.current?.value || "all";

        const params: Record<string, string> = {};

        if (searchValue) {
            params.search = searchValue;
        }

        if (statusValue !== "all") {
            params.is_active = statusValue;
        }

        setSearchParams(params);
    };

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const target = e.target as HTMLSelectElement;
        const statusValue = target.value;
        const searchValue = formRef.current?.search.value.trim() || "";

        const params: Record<string, string> = {};

        if (searchValue) {
            params.search = searchValue;
        }

        if (statusValue !== "all") {
            params.is_active = statusValue;
        }

        setSearchParams(params);
    };

    const clearFilters = () => {
        setSearchParams({});
        if (formRef.current) {
            formRef.current.search.value = "";
        }
        if (statusRef.current) {
            statusRef.current.value = "all";
        }
    };

    if (isLoading) return <FullscreenLoading />;
    if (error) return <p>Failed to load feature plans.</p>;

    return (
        <PageWrapper pageTitle="Available Packages" currentPage="Available Packages">
            <Card className="w-full mx-auto p-4">
                <div className="flex justify-end items-center mb-4">
                    <div className="flex gap-2 items-center">
                        <form
                            ref={formRef}
                            className="flex border rounded-md"
                            onSubmit={handleSearchSubmit}
                        >
                            <Input placeholder="Search packages..." className="border-none" name="search" />
                            <Button variant={"ghost"} type="submit">
                                <Search />
                            </Button>
                        </form>

                        <div className="relative">
                            <select
                                ref={statusRef}
                                onChange={handleStatusChange}
                                className="w-full rounded-md border border-input bg-background py-2 pl-3 pr-8 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&_[data-icon]]:size-4 appearance-none"
                            >
                                <option value="all">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none" />
                        </div>

                        {(searchParams.get("search") || searchParams.get("is_active")) && (
                            <Button onClick={clearFilters}>
                                Clear
                            </Button>
                        )}
                    </div>

                    <div className="ml-6">
                        <CreatePackageDialog />
                    </div>
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Package Name</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Price</TableHead>
                            <TableHead>Duration</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="flex justify-end">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {featurePlans?.data.map(
                            (item: {
                                plan_id: string;
                                plan_name: string;
                                is_active: boolean;
                                plan_created_at: string | number;
                                plan_price: string;
                                plan_description: string;
                                plans_interval: string;
                            }) => (
                                <TableRow key={item.plan_id} className="hover:bg-muted/20">
                                    <TableCell>{item.plan_name}</TableCell>
                                    <TableCell>
                                        <Badge className={cn(item.is_active ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800")}>
                                            {item.is_active ? "Active" : "Inactive"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{formatDate(new Date(item?.plan_created_at)) || item?.plan_created_at}</TableCell>
                                    <TableCell>${item?.plan_price}</TableCell>
                                    <TableCell className="capitalize">{item?.plans_interval || "N/A"}</TableCell>
                                    <TableCell>
                                        <p className="max-w-64 font-medium text-muted-foreground text-sm line-clamp-2 text-ellipsis">
                                            {item?.plan_description}
                                        </p>
                                    </TableCell>
                                    <TableCell className="flex justify-end">
                                        <div className="flex items-center gap-2">
                                            <BillingTableDropdown id={item.plan_id} />
                                        </div>
                                    </TableCell>
                                </TableRow>
                                // <li key={item.id}>
                                //     Plan: {item.plan_name} - Feature: {item.feature_name}
                                // </li>
                            )
                        )}
                    </TableBody>
                </Table>
            </Card>
        </PageWrapper>
    );
};

export default PackageManage;
